import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LimiteErro, IndicadorCarregamento } from './componentes/Comum';
import { Cabecalho } from './componentes/Layout';
import { useAppDispatch, useAppSelector } from './hooks/useRedux';
import { restaurarSessao } from './armazenamento';

/**
 * Componente principal da aplicação NexoGourmet
 * Define as rotas: /login, /painel e /admin
 * Requisitos: 6.2, 6.3, 6.4, 1.3, 1.5
 *
 * - Dispara restaurarSessao na inicialização para verificar localStorage
 * - Exibe loading enquanto sessão está sendo restaurada
 * - Renderiza rotas normalmente após restauração
 * - Rotas /admin protegidas por RotaProtegidaAdmin (verifica ehAdmin)
 */

// Importações lazy para otimização de bundle
const FormularioLogin = React.lazy(
  () => import('./componentes/Autenticacao/FormularioLogin')
);
const RotaProtegida = React.lazy(
  () => import('./componentes/Autenticacao/RotaProtegida')
);
const ContainerPainelControle = React.lazy(
  () => import('./componentes/PainelControle/ContainerPainelControle')
);

// Importações lazy para área administrativa
const RotaProtegidaAdmin = React.lazy(
  () => import('./componentes/Admin/RotaProtegidaAdmin')
);
const LayoutAdmin = React.lazy(
  () => import('./componentes/Admin/LayoutAdmin')
);
const PaginaLojistas = React.lazy(
  () => import('./componentes/Admin/PaginaLojistas')
);
const PaginaVinculos = React.lazy(
  () => import('./componentes/Admin/PaginaVinculos')
);
const PaginaEstabelecimentos = React.lazy(
  () => import('./componentes/Admin/PaginaEstabelecimentos')
);

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { carregando } = useAppSelector((estado) => estado.autenticacao);

  // Restaura sessão do localStorage na inicialização
  useEffect(() => {
    dispatch(restaurarSessao());
  }, [dispatch]);

  // Exibe loading enquanto a sessão está sendo restaurada
  if (carregando) {
    return (
      <LimiteErro>
        <IndicadorCarregamento mensagem="Carregando aplicação..." tamanho="grande" />
      </LimiteErro>
    );
  }

  return (
    <LimiteErro>
      <React.Suspense
        fallback={
          <IndicadorCarregamento mensagem="Carregando aplicação..." tamanho="grande" />
        }
      >
        <Routes>
          {/* Rota de login */}
          <Route path="/login" element={<FormularioLogin />} />

          {/* Rota protegida do painel */}
          <Route
            path="/painel"
            element={
              <RotaProtegida>
                <Cabecalho />
                <ContainerPainelControle />
              </RotaProtegida>
            }
          />

          {/* Rotas administrativas protegidas por RotaProtegidaAdmin */}
          <Route
            path="/admin"
            element={
              <RotaProtegidaAdmin>
                <LayoutAdmin />
              </RotaProtegidaAdmin>
            }
          >
            <Route index element={<Navigate to="/admin/lojistas" replace />} />
            <Route path="lojistas" element={<PaginaLojistas />} />
            <Route path="lojistas/:id/vinculos" element={<PaginaVinculos />} />
            <Route path="estabelecimentos" element={<PaginaEstabelecimentos />} />
          </Route>

          {/* Redireciona raiz para /painel */}
          <Route path="/" element={<Navigate to="/painel" replace />} />

          {/* Rota 404 - redireciona para /painel */}
          <Route path="*" element={<Navigate to="/painel" replace />} />
        </Routes>
      </React.Suspense>
    </LimiteErro>
  );
};

export default App;
